/**
 * 🎒 EQUIPMENT SYSTEM - UNIFIED EXPORTS
 *
 * All equipment organized by slot type:
 * - Weapons (21 items)
 * - Armor/Tops (15 items)
 * - Helmets (15 items)
 * - Bottoms (10 items)
 * - Gloves (10 items)
 * - Boots (15 items)
 * - Capes (10 items)
 * - Belts (10 items)
 * - Offhands (10 items)
 * - Pendants (10 items)
 * - Rings (15 items)
 */

// Import all equipment by slot type
import { WEAPONS } from './weapons';
import { ARMOR } from './armor';
import { HELMETS } from './helmets';
import { BOTTOMS } from './bottoms';
import { GLOVES } from './gloves';
import { BOOTS } from './boots';
import { CAPES } from './capes';
import { BELTS } from './belts';
import { OFFHAND } from './offhands';
import { PENDANTS } from './pendants';
import { RINGS } from './rings';
import { ALL_BOSS_UNIQUES, getBossUnique, getBossUniquesForBoss, isBossUnique } from './bossUniques';

// Re-export individual slot collections
export { WEAPONS } from './weapons';
export { ARMOR } from './armor';
export { HELMETS } from './helmets';
export { BOTTOMS } from './bottoms';
export { GLOVES } from './gloves';
export { BOOTS } from './boots';
export { CAPES } from './capes';
export { BELTS } from './belts';
export { OFFHAND } from './offhands';
export { PENDANTS } from './pendants';
export { RINGS } from './rings';

// Re-export boss unique equipment
export { ALL_BOSS_UNIQUES, getBossUnique, getBossUniquesForBoss, isBossUnique } from './bossUniques';

// ============================================================
// AGGREGATED EXPORTS
// ============================================================

// All equipment by slot (for slot-based lookups)
export const EQUIPMENT_BY_SLOT = {
  weapon: WEAPONS,
  offhand: OFFHAND,
  helmet: HELMETS,
  cape: CAPES,
  top: ARMOR,
  bottom: BOTTOMS,
  gloves: GLOVES,
  boots: BOOTS,
  belt: BELTS,
  pendant1: PENDANTS,
  ring1: RINGS,
};

// All equipment in one unified registry (including boss uniques)
export const ALL_EQUIPMENT = {
  ...WEAPONS,
  ...OFFHAND,
  ...HELMETS,
  ...CAPES,
  ...ARMOR,
  ...BOTTOMS,
  ...GLOVES,
  ...BOOTS,
  ...BELTS,
  ...PENDANTS,
  ...RINGS,
  ...ALL_BOSS_UNIQUES, // Boss-specific unique equipment
};

// ============================================================
// HELPER FUNCTIONS
// ============================================================

/**
 * Get all equipment for a specific slot
 */
export function getEquipmentBySlot(slot) {
  return EQUIPMENT_BY_SLOT[slot] || {};
}

/**
 * Get a single equipment item by ID
 */
export function getEquipmentById(id) {
  return ALL_EQUIPMENT[id] || null;
}

/**
 * Get all equipment of a specific rarity
 */
export function getEquipmentByRarity(rarity) {
  return Object.values(ALL_EQUIPMENT).filter(item => item.rarity === rarity);
}

/**
 * Get equipment by slot AND rarity
 */
export function getEquipmentBySlotAndRarity(slot, rarity) {
  const slotItems = EQUIPMENT_BY_SLOT[slot] || {};
  return Object.values(slotItems).filter(item => item.rarity === rarity);
}

/**
 * Get equipment count by slot
 */
export function getEquipmentCountBySlot() {
  return Object.entries(EQUIPMENT_BY_SLOT).reduce((acc, [slot, items]) => {
    acc[slot] = Object.keys(items).length;
    return acc;
  }, {});
}

/**
 * Get total equipment count
 */
export function getTotalEquipmentCount() {
  return Object.keys(ALL_EQUIPMENT).length;
}

/**
 * Get equipment by rarity and level range
 * Used for dungeon drops to ensure equipment matches dungeon difficulty
 * @param {string} rarity - Equipment rarity (common, uncommon, rare, epic, legendary, mythic)
 * @param {number} minLevel - Minimum level requirement (inclusive)
 * @param {number} maxLevel - Maximum level requirement (inclusive)
 * @returns {Array} Array of equipment items matching criteria
 */
export function getEquipmentByRarityAndLevel(rarity, minLevel, maxLevel) {
  return Object.values(ALL_EQUIPMENT).filter(item =>
    item.rarity === rarity &&
    item.levelReq >= minLevel &&
    item.levelReq <= maxLevel
  );
}

/**
 * Get a random equipment drop for a dungeon
 * Filters by rarity and dungeon level range
 * @param {string} rarity - Equipment rarity
 * @param {number} dungeonLevel - Base enemy level of the dungeon
 * @returns {Object|null} Random equipment item or null if none available
 */
export function getRandomEquipmentForDungeon(rarity, dungeonLevel) {
  // Define level range based on dungeon level
  // Allow equipment from (dungeonLevel - 5) to (dungeonLevel + 10)
  // This gives a reasonable range of drops appropriate for the dungeon
  const minLevel = Math.max(1, dungeonLevel - 5);
  const maxLevel = dungeonLevel + 10;

  const eligibleEquipment = getEquipmentByRarityAndLevel(rarity, minLevel, maxLevel);

  if (eligibleEquipment.length === 0) {
    // Fallback: if no equipment in range, try just by rarity
    const fallbackEquipment = getEquipmentByRarity(rarity);
    if (fallbackEquipment.length === 0) return null;
    return fallbackEquipment[Math.floor(Math.random() * fallbackEquipment.length)];
  }

  return eligibleEquipment[Math.floor(Math.random() * eligibleEquipment.length)];
}
